import React, { FC, useMemo } from "react";
import {
  Inline,
  View,
  useAppContext,
  useClientContext,
  useNavigation,
  useViewFilters,
  useGroups,
  UseGroupsContext,
  useViewContext,
} from "@data-root/react";
import { ScreenProps } from "../typings";
import { MapView as IMapView } from "@data-root/types";
import { viewStylesheet } from ".";
import { UseRowsContext, useAsyncCallback, useRows } from "@data-root/react";
import { Map } from "../components/inline/Map";
import FloatButton from "../components/FloatButton";
import { MoreOutlined, PlusOutlined } from "@ant-design/icons";
import { v4 } from "uuid";
import { ErrorView } from "@data-root/error-boundary";

export const MapView: FC<ScreenProps<IMapView>> = ({ view }) => {
  const { options, noAdd, tableId } = view;

  const { core } = useClientContext();
  const { appSpecifier, styles, getTable } = useAppContext();
  const { navigation } = useNavigation();

  const table = useMemo(() => getTable(tableId), [getTable, tableId]);

  const { filters: contextFilters, sort: contextSort } = useViewContext();
  const { filters: viewFilters, sort: viewSort } = useViewFilters(view);

  const groupsContext = useGroups(core, appSpecifier, tableId);
  const rowsContext = useRows(
    core,
    appSpecifier,
    tableId,
    [...contextFilters, ...viewFilters, ...groupsContext.filters],
    [...contextSort, ...viewSort]
  );

  const {
    getContext,
    displayRows,
    loaded,
    fetch,
    create,
    fetching,
    totalRows,
    error,
  } = rowsContext;

  const onNew = useAsyncCallback(async () => {
    const context = getContext();
    const { rowId } = await create({});
    const onNew = await core.evaluateFormula<string>(options.onNew, context);
    if (onNew) navigation.navigate(onNew, { rowId });
  }, [core, create, getContext, navigation, options.onNew]);

  const containerId = useMemo(() => v4(), []);

  if (error) return <ErrorView error={error} />;

  return (
    <UseRowsContext.Provider value={rowsContext}>
      <UseGroupsContext.Provider value={groupsContext}>
        <View
          style={{ ...viewStylesheet.container, ...viewStylesheet.vCenter }}
        >
          <View
            style={{
              ...viewStylesheet.contentContainer,
              ...viewStylesheet.limitedContentContainer,
              position: "relative",
            }}
          >
            <Inline id={containerId} table={table} view={view}>
              <Map
                containerId={containerId}
                getContext={getContext}
                loaded={loaded}
                totalRows={totalRows}
                rows={displayRows}
                fetching={fetching}
                onFetch={fetch}
              />
            </Inline>
          </View>
          <FloatButton.Group shape="square">
            <FloatButton
              shape="square"
              onClick={fetch}
              icon={<MoreOutlined style={{ color: styles.darkTintColor }} />}
            />

            {!noAdd && (
              <FloatButton
                shape="square"
                onClick={onNew}
                icon={<PlusOutlined style={{ color: styles.darkTintColor }} />}
              />
            )}
          </FloatButton.Group>
        </View>
      </UseGroupsContext.Provider>
    </UseRowsContext.Provider>
  );
};
